"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Incident = exports.IncidentClassName = void 0;
const tslib_1 = require("tslib");
const config_1 = tslib_1.__importDefault(require("@iotv/config"));
const constants_1 = require("../../constants");
const identity_1 = require("../../util/objects/identity");
const ModelCommon_1 = require("../ModelCommon");
const enums_1 = require("./enums");
const IncidentTypes_1 = require("./IncidentTypes");
const debug = !process.env.AWS_SESSION_TOKEN && false;
exports.IncidentClassName = 'Incident';
class Incident extends ModelCommon_1.ModelCommon {
    constructor(ob) {
        if (ob.type !== undefined && ob.type !== exports.IncidentClassName)
            throw new Error(`Tried to create a ${exports.IncidentClassName} from a ${ob.type} object!`);
        super(exports.IncidentClassName, { ...ob });
        this.type = ob.type ?? exports.IncidentClassName;
        this._status = ob._status;
        this._severity = ob._severity;
        this._incidentEntity = ob._incidentEntity;
        this._initiator = ob._initiator;
        this._incidentType = ob._incidentType;
        this._incidentSubtype = ob._incidentSubtype;
        this._summary = ob._summary;
        this._lastUpdated = ob.history?.at(0)?.timestamp ?? ob._lastUpdated;
        this.ttl = ob.ttl;
        this.timestamp = ob.timestamp;
        this.closedTimestamp = ob.closedTimestamp;
        this.history = ob.history;
        this.notificationRecords = ob.notificationRecords;
        this.updateDynamicKeys();
    }
    addToHistory(historyRecord) {
        this._lastUpdated = historyRecord.timestamp;
        if (!this.history) {
            this.history = [];
        }
        this.history.unshift(historyRecord);
        // purge when lots of events
        let i = this.history.length - 1;
        while (0 <= i && 25 < this.history.length) {
            const historyEvent = this.history.at(i)?.event;
            if (![IncidentTypes_1.IncidentEvent.OPENED, IncidentTypes_1.IncidentEvent.CLOSED].includes(historyEvent)) {
                this.history.pop();
            }
            i--;
        }
    }
    addNotificationRecord(notificationRecord) {
        if (!this.notificationRecords) {
            this.notificationRecords = {};
        }
        const acceptableDynamoDBKey = (0, identity_1.getAcceptableDynamoDBKey)(notificationRecord.recipient.sk);
        if (!this.notificationRecords[acceptableDynamoDBKey]) {
            this.notificationRecords[acceptableDynamoDBKey] = [];
        }
        this.notificationRecords[acceptableDynamoDBKey].push(notificationRecord);
    }
    /** Opens the incident and updates the timestamp, initiator defaults to the {@link SYSTEM | system} */
    openIncident(initiator = constants_1.SYSTEM) {
        const timestamp = Date.now();
        this.status = IncidentTypes_1.IncidentStatus.OPEN;
        this.timestampOpen = timestamp;
        this._lastUpdated = timestamp;
        const historyRecord = {
            timestamp,
            initiator,
            event: IncidentTypes_1.IncidentEvent.OPENED,
        };
        this.addToHistory(historyRecord);
        this.updateDynamicKeys();
    }
    /** Closes the incident and updates the timestamp, initiator defaults to the {@link SYSTEM | system} */
    closeIncident(initiator = constants_1.SYSTEM) {
        const timestamp = Date.now();
        this.status = IncidentTypes_1.IncidentStatus.CLOSED;
        this.closedTimestamp = timestamp;
        this._lastUpdated = timestamp;
        const historyRecord = {
            timestamp,
            initiator,
            event: IncidentTypes_1.IncidentEvent.CLOSED,
        };
        this.addToHistory(historyRecord);
        this.updateDynamicKeys();
    }
    // returns ghostified Incident for use when need correct dsk dpk of an incident for an alert trigger
    getIncidentZombieForAlert(alertTrigger) {
        let derivedIncident = undefined;
        const incidentEntity = alertTrigger.entityTriggeringAlert;
        const incidentType = alertTrigger.alertTriggerType && (enums_1.AlertIncidentType[alertTrigger.alertTriggerType] ?? enums_1.AlertIncidentType.SYSTEM_ISSUE);
        const { initiator, severity, status, summary, alertTriggerSubtype: incidentSubtype } = alertTrigger;
        if (initiator && incidentEntity && incidentType) {
            debug && console.log('getting dsk from initiator, severity, status, summary, alertTriggerSubtype, on alertTrigger ', alertTrigger);
            const derivedIncidentId = (0, IncidentTypes_1.getIncidentDSK)({
                initiator,
                incidentEntity,
                incidentType,
                incidentSubtype,
            });
            debug && console.log('derived dsk is ', derivedIncidentId);
            derivedIncident = {
                sk: config_1.default.app.ghostUuid,
                pk: incidentEntity.sk,
                dsk: derivedIncidentId,
                dpk: exports.IncidentClassName,
                type: exports.IncidentClassName,
                id: derivedIncidentId,
                _initiator: initiator,
                _incidentEntity: incidentEntity,
                _incidentType: incidentType,
                _incidentSubtype: incidentSubtype,
                _severity: severity,
                _status: status,
                _summary: summary,
                closedTimestamp: undefined,
                timestamp: undefined,
                ttl: undefined,
            };
        }
        else {
            debug && console.log('No initiator, severity, status, on alertTrigger', alertTrigger);
        }
        debug && console.log('returning derivedIncident', derivedIncident);
        return derivedIncident;
    }
    recordAlertTrigger(alertTrigger) {
        const timestamp = Date.now();
        this._lastUpdated = timestamp;
        const historyRecord = {
            initiator: alertTrigger.initiator ?? constants_1.SYSTEM,
            timestamp,
            event: alertTrigger.alertTriggerType ?? IncidentTypes_1.IncidentEvent.UPDATED,
            summary: alertTrigger.summary,
            alertSk: alertTrigger.sk,
        };
        this.addToHistory(historyRecord);
        this.updateDynamicKeys();
    }
    updateDynamicKeys() {
        this.dpk = this.type;
        const { incidentEntity, incidentType, incidentSubtype, initiator } = this;
        if (incidentEntity && incidentType && initiator) {
            this.dsk = (0, IncidentTypes_1.getIncidentDSK)({
                incidentEntity,
                incidentType,
                incidentSubtype,
                initiator,
            });
        }
        else {
            debug &&
                console.warn('WARN updateDynamicKeys something missing', {
                    incidentEntity,
                    incidentType,
                    initiator,
                });
        }
    }
    get summary() {
        return this._summary;
    }
    set summary(summary) {
        this._summary = summary;
    }
    get incidentEntity() {
        return this._incidentEntity;
    }
    set incidentEntity(entity) {
        this._incidentEntity = entity;
    }
    get incidentEntityName() {
        return this._incidentEntity?.name;
    }
    set incidentEntityName(name) {
        if (this._incidentEntity)
            this._incidentEntity.name = name;
    }
    get status() {
        return this._status;
    }
    set status(newStatus) {
        this._status = newStatus;
        this.updateDynamicKeys();
    }
    get severity() {
        return this._severity;
    }
    set severity(newSeverity) {
        this._severity = newSeverity;
        this.updateDynamicKeys();
    }
    get initiator() {
        return this._initiator;
    }
    set initiator(initiator) {
        this._initiator = initiator;
    }
    get incidentType() {
        return this._incidentType;
    }
    set incidentType(incidentType) {
        this._incidentType = incidentType;
    }
    get incidentSubtype() {
        return this._incidentSubtype;
    }
    set incidentSubtype(incidentSubtype) {
        this._incidentSubtype = incidentSubtype;
    }
    get timestampOpen() {
        return this.timestamp ?? undefined;
    }
    set timestampOpen(timestamp) {
        this.timestamp = timestamp;
    }
    get lastUpdated() {
        return this.history?.at(0)?.timestamp ?? this._lastUpdated;
    }
    getUserAttributes() {
        const atts = {
            ...super.getUserAttributes(),
            _incidentType: {
                type: 'String',
                label: 'Incident Type',
                editable: false,
                displayFn: (v) => enums_1.IncidentTypeNames[v],
            },
            _severity: {
                type: 'String',
                label: 'Severity',
                editable: false,
                displayFn: (v) => v.slice(0, 1) + v.slice(1).toLowerCase().replaceAll('_', ' '),
            },
            _status: {
                type: 'String',
                label: 'Status',
                editable: false,
                displayFn: (v) => v.slice(0, 1) + v.slice(1).toLowerCase().replaceAll('_', ' '),
            },
            timestamp: {
                type: 'Date',
                label: 'Opened at',
                editable: false,
            },
            lastUpdated: {
                type: 'Date',
                label: 'Last updated',
                editable: false,
            },
            _summary: {
                type: 'String',
                label: 'Summary',
                editable: false,
            },
        };
        delete atts.description;
        delete atts.type;
        if (this.status === IncidentTypes_1.IncidentStatus.CLOSED) {
            atts.closedTimestamp = {
                type: 'Date',
                label: 'Closed at',
                editable: false,
            };
        }
        return atts;
    }
}
exports.Incident = Incident;
